export const ROUTE_PATHS = {
  HOME: '/'
} as const;

export type Language = 'ca' | 'es' | 'en';

export type Translations = {
  nav: {
    hero: string;
    about: string;
    problem: string;
    solution: string;
    services: string;
    product: string;
    sustainability: string;
    howItWorks: string;
    gallery: string;
    location: string;
    contact: string;
  };
  hero: {
    title: string;
    subtitle: string;
    cta: string;
  };
  about: {
    title: string;
    description: string;
  };
  problem: {
    title: string;
    items: {
      cost: { title: string; description: string };
      inefficiency: { title: string; description: string };
      innovation: { title: string; description: string };
    };
  };
  solution: {
    title: string;
    description: string;
    keyPhrase: string;
  };
  services: {
    title: string;
    items: {
      parquet: { title: string; description: string };
      turf: { title: string; description: string };
      maintenance: { title: string; description: string };
      consulting: { title: string; description: string };
    };
  };
  product: {
    title: string;
    features: {
      energy: { title: string; description: string };
      cost: { title: string; description: string };
      terrain: { title: string; description: string };
      tech: { title: string; description: string };
    };
  };
  sustainability: {
    title: string;
    subtitle: string;
    sdgs: {
      sdg7: { name: string; description: string };
      sdg9: { name: string; description: string };
      sdg11: { name: string; description: string };
      sdg12: { name: string; description: string };
    };
  };
  howItWorks: {
    title: string;
    steps: {
      step1: { title: string; description: string };
      step2: { title: string; description: string };
      step3: { title: string; description: string };
    };
  };
  gallery: {
    title: string;
  };
  location: {
    title: string;
    address: string;
    city: string;
  };
  contact: {
    title: string;
    form: {
      name: string;
      email: string;
      phone: string;
      message: string;
      submit: string;
    };
    info: {
      email: string;
      phone: string;
    };
    cta: string;
  };
  footer: {
    copyright: string;
    slogan: string;
  };
};

export const translations: Record<Language, Translations> = {
  ca: {
    nav: {
      hero: 'Inici',
      about: 'Sobre el projecte',
      problem: 'El problema',
      solution: 'La solució',
      services: 'Serveis',
      product: 'Producte',
      sustainability: 'Sostenibilitat',
      howItWorks: 'Com funciona',
      gallery: 'Galeria',
      location: 'Ubicació',
      contact: 'Contacte'
    },
    hero: {
      title: 'Transformam cada petjada en energia',
      subtitle: 'Tecnologia sostenible per a instal·lacions esportives',
      cta: 'Descobreix com funciona'
    },
    about: {
      title: 'Sobre el projecte',
      description: 'Som un grup de joves apassionats per l\'esport que hem creat una solució innovadora per reduir el cost energètic i millorar les instal·lacions esportives. La nostra missió és transformar el moviment dels esportistes en energia neta i sostenible.'
    },
    problem: {
      title: 'El problema',
      items: {
        cost: {
          title: 'Alt cost de l\'electricitat',
          description: 'Les instal·lacions esportives tenen un consum energètic molt elevat que impacta directament en els pressupostos.'
        },
        inefficiency: {
          title: 'Instal·lacions poc eficients',
          description: 'Moltes infraestructures esportives no aprofiten les noves tecnologies per optimitzar el seu rendiment energètic.'
        },
        innovation: {
          title: 'Falta d\'innovació sostenible',
          description: 'El sector esportiu necessita solucions tecnològiques que combinin rendiment i sostenibilitat ambiental.'
        }
      }
    },
    solution: {
      title: 'La solució',
      description: 'Paviments esportius amb sensors que generen electricitat a partir de les petjades dels esportistes. Cada moviment es converteix en energia útil per a la instal·lació.',
      keyPhrase: 'El moviment es converteix en energia útil'
    },
    services: {
      title: 'Els nostres serveis',
      items: {
        parquet: {
          title: 'Instal·lació de parquet intel·ligent',
          description: 'Paviments esportius d\'alta qualitat amb tecnologia de captació d\'energia cinètica integrada.'
        },
        turf: {
          title: 'Gespa artificial amb generació energètica',
          description: 'Superfícies de gespa artificial que transformen cada petjada en electricitat neta.'
        },
        maintenance: {
          title: 'Manteniment tècnic',
          description: 'Servei complet de manteniment i optimització dels sistemes de generació energètica.'
        },
        consulting: {
          title: 'Assessorament',
          description: 'Consultoria especialitzada per implementar solucions energètiques sostenibles en instal·lacions esportives.'
        }
      }
    },
    product: {
      title: 'Producte diferencial',
      features: {
        energy: {
          title: 'Genera energia neta',
          description: 'Cada petjada es converteix en electricitat renovable per a la instal·lació.'
        },
        cost: {
          title: 'Redueix costos',
          description: 'Estalvi significatiu en la factura elèctrica gràcies a l\'autogeneració d\'energia.'
        },
        terrain: {
          title: 'Millora el terreny',
          description: 'Paviments d\'alta qualitat que ofereixen millor rendiment esportiu i durabilitat.'
        },
        tech: {
          title: 'Tecnologia innovadora',
          description: 'Sensors avançats i sistemes intel·ligents de captació i emmagatzematge d\'energia.'
        }
      }
    },
    sustainability: {
      title: 'Sostenibilitat',
      subtitle: 'Compromesos amb l\'Agenda 2030',
      sdgs: {
        sdg7: {
          name: 'Energia neta i assequible',
          description: 'Els nostres paviments intel·ligents generen electricitat renovable a partir de les pisades, contribuint directament a l\'accés a energia neta i assequible per a les instal·lacions esportives.'
        },
        sdg9: {
          name: 'Indústria, innovació i infraestructura',
          description: 'Dissenyem infraestructures esportives de nova generació que integren tecnologia piezoelèctrica avançada, impulsant la innovació al sector esportiu i la indústria de la construcció sostenible.'
        },
        sdg11: {
          name: 'Ciutats i comunitats sostenibles',
          description: 'Ajudem als ajuntaments i clubs esportius a convertir les seves instal·lacions en espais eficients i sostenibles, contribuint a ciutats més intel·ligents amb menor empremta de carboni.'
        },
        sdg12: {
          name: 'Producció i consum responsables',
          description: 'Reduïm el consum d\'energia elèctrica de la xarxa a través de l\'autogeneració, promovent un model de consum responsable i una economia circular en l\'esport.'
        }
      }
    },
    howItWorks: {
      title: 'Com funciona',
      steps: {
        step1: {
          title: 'Petjada',
          description: 'L\'esportista pisa el paviment intel·ligent durant l\'activitat física.'
        },
        step2: {
          title: 'Captació d\'energia',
          description: 'Els sensors integrats capten l\'energia cinètica generada pel moviment.'
        },
        step3: {
          title: 'Transformació en electricitat',
          description: 'L\'energia captada es converteix en electricitat útil per a la instal·lació.'
        }
      }
    },
    gallery: {
      title: 'Galeria'
    },
    location: {
      title: 'Ubicació',
      address: 'Carrer Santiago de Rusiñol',
      city: 'Inca, Mallorca'
    },
    contact: {
      title: 'Contacte',
      form: {
        name: 'Nom',
        email: 'Correu electrònic',
        phone: 'Telèfon',
        message: 'Missatge',
        submit: 'Enviar'
      },
      info: {
        email: 'info@sportenergyfloor.com',
        phone: '+34 971 123 456'
      },
      cta: 'Sol·licita informació'
    },
    footer: {
      copyright: '© 2026 Sport Energy Floor. Tots els drets reservats.',
      slogan: 'La teva petjada és energia'
    }
  },
  es: {
    nav: {
      hero: 'Inicio',
      about: 'Sobre el proyecto',
      problem: 'El problema',
      solution: 'La solución',
      services: 'Servicios',
      product: 'Producto',
      sustainability: 'Sostenibilidad',
      howItWorks: 'Cómo funciona',
      gallery: 'Galería',
      location: 'Ubicación',
      contact: 'Contacto'
    },
    hero: {
      title: 'Transformamos cada pisada en energía',
      subtitle: 'Tecnología sostenible para instalaciones deportivas',
      cta: 'Descubre cómo funciona'
    },
    about: {
      title: 'Sobre el proyecto',
      description: 'Somos un grupo de jóvenes apasionados por el deporte que hemos creado una solución innovadora para reducir el coste energético y mejorar las instalaciones deportivas. Nuestra misión es transformar el movimiento de los deportistas en energía limpia y sostenible.'
    },
    problem: {
      title: 'El problema',
      items: {
        cost: {
          title: 'Alto coste de la electricidad',
          description: 'Las instalaciones deportivas tienen un consumo energético muy elevado que impacta directamente en los presupuestos.'
        },
        inefficiency: {
          title: 'Instalaciones poco eficientes',
          description: 'Muchas infraestructuras deportivas no aprovechan las nuevas tecnologías para optimizar su rendimiento energético.'
        },
        innovation: {
          title: 'Falta de innovación sostenible',
          description: 'El sector deportivo necesita soluciones tecnológicas que combinen rendimiento y sostenibilidad ambiental.'
        }
      }
    },
    solution: {
      title: 'La solución',
      description: 'Pavimentos deportivos con sensores que generan electricidad a partir de las pisadas de los deportistas. Cada movimiento se convierte en energía útil para la instalación.',
      keyPhrase: 'El movimiento se convierte en energía útil'
    },
    services: {
      title: 'Nuestros servicios',
      items: {
        parquet: {
          title: 'Instalación de parquet inteligente',
          description: 'Pavimentos deportivos de alta calidad con tecnología de captación de energía cinética integrada.'
        },
        turf: {
          title: 'Césped artificial con generación energética',
          description: 'Superficies de césped artificial que transforman cada pisada en electricidad limpia.'
        },
        maintenance: {
          title: 'Mantenimiento técnico',
          description: 'Servicio completo de mantenimiento y optimización de los sistemas de generación energética.'
        },
        consulting: {
          title: 'Asesoramiento',
          description: 'Consultoría especializada para implementar soluciones energéticas sostenibles en instalaciones deportivas.'
        }
      }
    },
    product: {
      title: 'Producto diferencial',
      features: {
        energy: {
          title: 'Genera energía limpia',
          description: 'Cada pisada se convierte en electricidad renovable para la instalación.'
        },
        cost: {
          title: 'Reduce costes',
          description: 'Ahorro significativo en la factura eléctrica gracias a la autogeneración de energía.'
        },
        terrain: {
          title: 'Mejora el terreno',
          description: 'Pavimentos de alta calidad que ofrecen mejor rendimiento deportivo y durabilidad.'
        },
        tech: {
          title: 'Tecnología innovadora',
          description: 'Sensores avanzados y sistemas inteligentes de captación y almacenamiento de energía.'
        }
      }
    },
    sustainability: {
      title: 'Sostenibilidad',
      subtitle: 'Comprometidos con la Agenda 2030',
      sdgs: {
        sdg7: {
          name: 'Energía limpia y asequible',
          description: 'Nuestros pavimentos inteligentes generan electricidad renovable a partir de las pisadas, contribuyendo directamente al acceso a energía limpia y asequible para las instalaciones deportivas.'
        },
        sdg9: {
          name: 'Industria, innovación e infraestructura',
          description: 'Diseñamos infraestructuras deportivas de nueva generación que integran tecnología piezoeléctrica avanzada, impulsando la innovación en el sector deportivo y la industria de la construcción sostenible.'
        },
        sdg11: {
          name: 'Ciudades y comunidades sostenibles',
          description: 'Ayudamos a ayuntamientos y clubes deportivos a convertir sus instalaciones en espacios eficientes y sostenibles, contribuyendo a ciudades más inteligentes con menor huella de carbono.'
        },
        sdg12: {
          name: 'Producción y consumo responsables',
          description: 'Reducimos el consumo de energía eléctrica de la red mediante la autogeneración, promoviendo un modelo de consumo responsable y una economía circular en el deporte.'
        }
      }
    },
    howItWorks: {
      title: 'Cómo funciona',
      steps: {
        step1: {
          title: 'Pisada',
          description: 'El deportista pisa el pavimento inteligente durante la actividad física.'
        },
        step2: {
          title: 'Captación de energía',
          description: 'Los sensores integrados captan la energía cinética generada por el movimiento.'
        },
        step3: {
          title: 'Transformación en electricidad',
          description: 'La energía captada se convierte en electricidad útil para la instalación.'
        }
      }
    },
    gallery: {
      title: 'Galería'
    },
    location: {
      title: 'Ubicación',
      address: 'Carrer Santiago de Rusiñol',
      city: 'Inca, Mallorca'
    },
    contact: {
      title: 'Contacto',
      form: {
        name: 'Nombre',
        email: 'Correo electrónico',
        phone: 'Teléfono',
        message: 'Mensaje',
        submit: 'Enviar'
      },
      info: {
        email: 'info@sportenergyfloor.com',
        phone: '+34 971 123 456'
      },
      cta: 'Solicita información'
    },
    footer: {
      copyright: '© 2026 Sport Energy Floor. Todos los derechos reservados.',
      slogan: 'Tu pisada es energía'
    }
  },
  en: {
    nav: {
      hero: 'Home',
      about: 'About',
      problem: 'The Problem',
      solution: 'The Solution',
      services: 'Services',
      product: 'Product',
      sustainability: 'Sustainability',
      howItWorks: 'How It Works',
      gallery: 'Gallery',
      location: 'Location',
      contact: 'Contact'
    },
    hero: {
      title: 'We turn every step into energy',
      subtitle: 'Sustainable technology for sports facilities',
      cta: 'Discover how it works'
    },
    about: {
      title: 'About the project',
      description: 'We are a group of young people passionate about sports who have created an innovative solution to reduce energy costs and improve sports facilities. Our mission is to transform athletes\' movement into clean and sustainable energy.'
    },
    problem: {
      title: 'The Problem',
      items: {
        cost: {
          title: 'High electricity costs',
          description: 'Sports facilities have very high energy consumption that directly impacts budgets.'
        },
        inefficiency: {
          title: 'Inefficient facilities',
          description: 'Many sports infrastructures do not take advantage of new technologies to optimize their energy performance.'
        },
        innovation: {
          title: 'Lack of sustainable innovation',
          description: 'The sports sector needs technological solutions that combine performance and environmental sustainability.'
        }
      }
    },
    solution: {
      title: 'The Solution',
      description: 'Sports flooring with sensors that generate electricity from athletes\' footsteps. Every movement is converted into useful energy for the facility.',
      keyPhrase: 'Movement becomes useful energy'
    },
    services: {
      title: 'Our Services',
      items: {
        parquet: {
          title: 'Smart parquet installation',
          description: 'High-quality sports flooring with integrated kinetic energy capture technology.'
        },
        turf: {
          title: 'Artificial turf with energy generation',
          description: 'Artificial turf surfaces that transform every step into clean electricity.'
        },
        maintenance: {
          title: 'Technical maintenance',
          description: 'Complete maintenance and optimization service for energy generation systems.'
        },
        consulting: {
          title: 'Consulting',
          description: 'Specialized consulting to implement sustainable energy solutions in sports facilities.'
        }
      }
    },
    product: {
      title: 'Differential Product',
      features: {
        energy: {
          title: 'Generates clean energy',
          description: 'Every step is converted into renewable electricity for the facility.'
        },
        cost: {
          title: 'Reduces costs',
          description: 'Significant savings on electricity bills thanks to energy self-generation.'
        },
        terrain: {
          title: 'Improves terrain',
          description: 'High-quality flooring that offers better sports performance and durability.'
        },
        tech: {
          title: 'Innovative technology',
          description: 'Advanced sensors and intelligent energy capture and storage systems.'
        }
      }
    },
    sustainability: {
      title: 'Sustainability',
      subtitle: 'Committed to the 2030 Agenda',
      sdgs: {
        sdg7: {
          name: 'Affordable and clean energy',
          description: 'Our smart flooring generates renewable electricity from footsteps, directly contributing to access to clean and affordable energy for sports facilities.'
        },
        sdg9: {
          name: 'Industry, innovation and infrastructure',
          description: 'We design next-generation sports infrastructures integrating advanced piezoelectric technology, driving innovation in the sports sector and the sustainable construction industry.'
        },
        sdg11: {
          name: 'Sustainable cities and communities',
          description: 'We help municipalities and sports clubs turn their facilities into efficient and sustainable spaces, contributing to smarter cities and communities with a lower carbon footprint.'
        },
        sdg12: {
          name: 'Responsible consumption and production',
          description: 'We reduce grid electricity consumption through self-generation, promoting a responsible consumption model and a circular economy in sport.'
        }
      }
    },
    howItWorks: {
      title: 'How It Works',
      steps: {
        step1: {
          title: 'Footstep',
          description: 'The athlete steps on the smart flooring during physical activity.'
        },
        step2: {
          title: 'Energy capture',
          description: 'Integrated sensors capture the kinetic energy generated by movement.'
        },
        step3: {
          title: 'Transformation into electricity',
          description: 'The captured energy is converted into useful electricity for the facility.'
        }
      }
    },
    gallery: {
      title: 'Gallery'
    },
    location: {
      title: 'Location',
      address: 'Carrer Santiago de Rusiñol',
      city: 'Inca, Mallorca'
    },
    contact: {
      title: 'Contact',
      form: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        message: 'Message',
        submit: 'Send'
      },
      info: {
        email: 'info@sportenergyfloor.com',
        phone: '+34 971 123 456'
      },
      cta: 'Request information'
    },
    footer: {
      copyright: '© 2026 Sport Energy Floor. All rights reserved.',
      slogan: 'Your step is energy'
    }
  }
};

export const seoMeta: Record<Language, { title: string; description: string; keywords: string }> = {
  ca: {
    title: 'Sport Energy Floor | Paviment esportiu intel·ligent',
    description: 'Transformam cada petjada en energia. Tecnologia sostenible per a instal·lacions esportives amb paviments intel·ligents que generen electricitat.',
    keywords: 'paviment esportiu intel·ligent, energia cinètica esport, estalvi energia instal·lacions esportives, tecnologia sostenible, paviment intel·ligent Mallorca'
  },
  es: {
    title: 'Sport Energy Floor | Pavimento deportivo inteligente',
    description: 'Transformamos cada pisada en energía. Tecnología sostenible para instalaciones deportivas con pavimentos inteligentes que generan electricidad.',
    keywords: 'pavimento deportivo inteligente, energía cinética deporte, ahorro energía instalaciones deportivas, tecnología sostenible, pavimento inteligente Mallorca'
  },
  en: {
    title: 'Sport Energy Floor | Smart sports flooring',
    description: 'We turn every step into energy. Sustainable technology for sports facilities with smart flooring that generates electricity.',
    keywords: 'smart sports flooring, kinetic energy sports, energy saving sports facilities, sustainable technology, smart flooring Mallorca'
  }
};

export const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};